/**
 * Skill Profiles — parameter tables for AI difficulty levels 1-9.
 *
 * Low skill = slow, inaccurate, random targeting.
 * High skill = fast, precise, optimal targeting.
 */

export interface SkillProfile {
  /** Frames of "thinking" delay before acting. */
  thinkFrames: number;
  /** Number of angle samples for trajectory solver. */
  angleSamples: number;
  /** Number of power samples for trajectory solver. */
  powerSamples: number;
  /** Gaussian noise added to final aim angle (radians). */
  aimNoise: number;
  /** Gaussian noise added to final power (0-1 scale). */
  powerNoise: number;
  /** Probability (0-1) of picking the optimal target vs random. */
  targetOptimalPct: number;
  /** DI quality — lerp factor between random and optimal DI direction (0-1). */
  diQuality: number;
  /** Whether AI uses voluntary special (burst). */
  usesSpecial: boolean;
  /** Edgeguard aim noise (radians). */
  edgeguardAimNoise: number;
  /** Angle threshold — stop aiming when within this many radians of target. */
  aimThreshold: number;
  /** Power threshold — release charge when within this range of target power. */
  powerThreshold: number;
  /** Frames of reaction delay before acting in reactive phases (DI, burst, edgeguard, defense). */
  reactionFrames: number;
  /** ±variance on reactionFrames (seeded RNG). */
  reactionVariance: number;
}

const PROFILES: Record<number, SkillProfile> = {
  1: {
    thinkFrames: 120,
    angleSamples: 20,
    powerSamples: 6,
    aimNoise: 0.06,
    powerNoise: 0.30,
    targetOptimalPct: 0.30,
    diQuality: 0.1,
    usesSpecial: false,
    edgeguardAimNoise: 0.40,
    aimThreshold: 0.12,
    powerThreshold: 0.20,
    reactionFrames: 50,
    reactionVariance: 15,
  },
  2: {
    thinkFrames: 100,
    angleSamples: 22,
    powerSamples: 6,
    aimNoise: 0.05,
    powerNoise: 0.24,
    targetOptimalPct: 0.40,
    diQuality: 0.2,
    usesSpecial: false,
    edgeguardAimNoise: 0.30,
    aimThreshold: 0.10,
    powerThreshold: 0.16,
    reactionFrames: 42,
    reactionVariance: 12,
  },
  3: {
    thinkFrames: 75,
    angleSamples: 24,
    powerSamples: 6,
    aimNoise: 0.04,
    powerNoise: 0.18,
    targetOptimalPct: 0.50,
    diQuality: 0.3,
    usesSpecial: false,
    edgeguardAimNoise: 0.22,
    aimThreshold: 0.08,
    powerThreshold: 0.12,
    reactionFrames: 35,
    reactionVariance: 10,
  },
  4: {
    thinkFrames: 55,
    angleSamples: 26,
    powerSamples: 7,
    aimNoise: 0.03,
    powerNoise: 0.14,
    targetOptimalPct: 0.60,
    diQuality: 0.45,
    usesSpecial: false,
    edgeguardAimNoise: 0.16,
    aimThreshold: 0.06,
    powerThreshold: 0.10,
    reactionFrames: 28,
    reactionVariance: 8,
  },
  5: {
    thinkFrames: 45,
    angleSamples: 28,
    powerSamples: 7,
    aimNoise: 0.025,
    powerNoise: 0.10,
    targetOptimalPct: 0.70,
    diQuality: 0.6,
    usesSpecial: true,
    edgeguardAimNoise: 0.12,
    aimThreshold: 0.05,
    powerThreshold: 0.08,
    reactionFrames: 22,
    reactionVariance: 6,
  },
  6: {
    thinkFrames: 35,
    angleSamples: 30,
    powerSamples: 7,
    aimNoise: 0.02,
    powerNoise: 0.07,
    targetOptimalPct: 0.80,
    diQuality: 0.7,
    usesSpecial: true,
    edgeguardAimNoise: 0.08,
    aimThreshold: 0.04,
    powerThreshold: 0.06,
    reactionFrames: 16,
    reactionVariance: 5,
  },
  7: {
    thinkFrames: 22,
    angleSamples: 32,
    powerSamples: 8,
    aimNoise: 0.015,
    powerNoise: 0.05,
    targetOptimalPct: 0.90,
    diQuality: 0.8,
    usesSpecial: true,
    edgeguardAimNoise: 0.05,
    aimThreshold: 0.03,
    powerThreshold: 0.04,
    reactionFrames: 12,
    reactionVariance: 4,
  },
  8: {
    thinkFrames: 16,
    angleSamples: 34,
    powerSamples: 8,
    aimNoise: 0.01,
    powerNoise: 0.03,
    targetOptimalPct: 0.95,
    diQuality: 0.9,
    usesSpecial: true,
    edgeguardAimNoise: 0.03,
    aimThreshold: 0.02,
    powerThreshold: 0.025,
    reactionFrames: 8,
    reactionVariance: 3,
  },
  9: {
    thinkFrames: 12,
    angleSamples: 36,
    powerSamples: 8,
    aimNoise: 0.005,
    powerNoise: 0.015,
    targetOptimalPct: 1.0,
    diQuality: 1.0,
    usesSpecial: true,
    edgeguardAimNoise: 0.01,
    aimThreshold: 0.015,
    powerThreshold: 0.015,
    reactionFrames: 4,
    reactionVariance: 2,
  },
};

export function getSkillProfile(difficulty: number): SkillProfile {
  const clamped = Math.max(1, Math.min(9, Math.round(difficulty)));
  return PROFILES[clamped];
}
